/**
 * 유저 이용권 이력 — DB가 소스 오브 트루스, localStorage는 updated_at/revision 캐시.
 */
import { supabase } from '../supabaseClient';
import {
  buildSubscriptionPassStateRecord,
  loadSubscriptionPassCacheMeta,
  loadSubscriptionPassState,
  parseSubscriptionPassStateRecord,
  saveSubscriptionPassState,
  type SubscriptionPassPersistedState,
} from '../utils/subscriptionPassStorage';

export type SubscriptionPassSyncSource = 'server' | 'cache' | 'empty' | 'error';

export type SubscriptionPassSyncResult = {
  state: SubscriptionPassPersistedState;
  source: SubscriptionPassSyncSource;
};

function emptyState(): SubscriptionPassPersistedState {
  return {
    individualHistory: [],
    groupHistory: [],
    eventHistory: [],
    eventPassPeriod: null,
    sessionGroupPassBinding: null,
  };
}

function stateFromDbRow(row: Record<string, unknown>): SubscriptionPassPersistedState {
  return parseSubscriptionPassStateRecord({
    individualHistory: row.individual_history,
    groupHistory: row.group_history,
    eventHistory: row.event_history,
    eventPassPeriod: row.event_pass_period,
    sessionGroupPassBinding: row.session_group_pass_binding,
  });
}

function dbPayloadFromState(state: SubscriptionPassPersistedState): Record<string, unknown> {
  const rec = buildSubscriptionPassStateRecord(state);
  return {
    individual_history: rec.individualHistory,
    group_history: rec.groupHistory,
    event_history: rec.eventHistory,
    event_pass_period: rec.eventPassPeriod,
    session_group_pass_binding: rec.sessionGroupPassBinding,
  };
}

function isEmptyPassState(state: SubscriptionPassPersistedState): boolean {
  return (
    state.individualHistory.length === 0 &&
    state.groupHistory.length === 0 &&
    state.eventHistory.length === 0 &&
    !state.eventPassPeriod &&
    !state.sessionGroupPassBinding
  );
}

/**
 * DB 기준 동기화.
 * - 캐시 revision/updated_at이 서버와 같으면 검증된 캐시 사용
 * - 서버 row 없음 → empty(FREE)로 맞춤 (로컬 위조 업로드 금지)
 * - 그 외 서버 상태로 캐시 갱신
 */
export async function syncMySubscriptionPassState(
  userId: string
): Promise<SubscriptionPassSyncResult> {
  const uid = userId.trim();
  if (!uid) return { state: emptyState(), source: 'empty' };

  const cacheMeta = loadSubscriptionPassCacheMeta(uid);
  const cached = loadSubscriptionPassState(uid);

  const { data: head, error: headError } = await supabase
    .from('platform_user_subscription_passes')
    .select('updated_at, revision')
    .eq('user_id', uid)
    .maybeSingle();

  if (headError) {
    console.error('[subscription-pass] head fetch failed', headError);
    if (cacheMeta.serverRevision > 0 && cacheMeta.serverUpdatedAt) {
      return { state: cached, source: 'cache' };
    }
    return { state: emptyState(), source: 'error' };
  }

  if (
    head &&
    typeof head.updated_at === 'string' &&
    typeof head.revision === 'number' &&
    cacheMeta.serverUpdatedAt === head.updated_at &&
    cacheMeta.serverRevision === head.revision
  ) {
    return { state: cached, source: 'cache' };
  }

  if (!head) {
    saveSubscriptionPassState(emptyState(), uid, {
      serverUpdatedAt: null,
      serverRevision: 0,
    });
    return { state: emptyState(), source: 'empty' };
  }

  const { data: row, error } = await supabase
    .from('platform_user_subscription_passes')
    .select(
      'individual_history, group_history, event_history, event_pass_period, session_group_pass_binding, updated_at, revision'
    )
    .eq('user_id', uid)
    .maybeSingle();

  if (error || !row) {
    console.error('[subscription-pass] row fetch failed', error);
    if (cacheMeta.serverRevision > 0 && cacheMeta.serverUpdatedAt) {
      return { state: cached, source: 'cache' };
    }
    return { state: emptyState(), source: 'error' };
  }

  const state = stateFromDbRow(row as Record<string, unknown>);
  saveSubscriptionPassState(state, uid, {
    serverUpdatedAt: typeof row.updated_at === 'string' ? row.updated_at : null,
    serverRevision:
      typeof row.revision === 'number' && Number.isFinite(row.revision)
        ? Math.floor(row.revision)
        : 0,
  });
  return { state, source: 'server' };
}

/**
 * 이용권 쓰기는 서버에서만 수행된다(쿠폰 등록 RPC / 관리자 RPC / service_role).
 * 클라이언트 로컬 상태는 DB 에 쓰지 않고, 서버 상태로 캐시를 다시 맞춘다.
 * (보안: 20261020210000_lock_subscription_pass_writes — upsert_my_subscription_pass 제거)
 */
export async function persistMySubscriptionPassState(
  userId: string,
  state: SubscriptionPassPersistedState
): Promise<SubscriptionPassPersistedState> {
  const uid = userId.trim();
  if (!uid) return state;
  const result = await syncMySubscriptionPassState(uid);
  return result.state;
}

/** 관리자 전용: 대상 유저 이용권 row upsert (DB 에서 is_platform_admin() 검사) */
export async function adminUpsertSubscriptionPassState(
  targetUserId: string,
  state: SubscriptionPassPersistedState
): Promise<SubscriptionPassPersistedState> {
  const { data, error } = await supabase.rpc('admin_upsert_subscription_pass', {
    p_user_id: targetUserId.trim(),
    payload: dbPayloadFromState(state),
  });
  if (error || !data) {
    console.error('[subscription-pass] admin upsert failed', error);
    throw error ?? new Error('admin subscription pass upsert failed');
  }
  return stateFromDbRow(data as Record<string, unknown>);
}
